import axios from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import {
  isReauthorizeRequired,
  SheetsReauthorizeRequiredError,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";

// Wire shapes mirror `common_core::gsheet_export` (serde tag = "kind",
// snake_case). The server deserializes `table_version` from a bare number.

/// Google Sheets caps a spreadsheet at 10,000,000 cells. Mirrors the server's
/// `MAX_TOTAL_CELLS` preflight so the dialog can reject an oversized export up
/// front instead of after a failed write.
export const GOOGLE_SHEETS_MAX_CELLS = 10_000_000;

/// Above this (but under the hard cap), the Sheets write — sequential API calls
/// server-side — takes long enough to warn the user and point them at the
/// faster CSV/Parquet download. Advisory only; the export still proceeds.
export const GOOGLE_SHEETS_SLOW_CELLS = 1_000_000;

export type SheetTabTarget =
  { kind: "new_tab"; title: string } | { kind: "overwrite_tab"; title: string };

export type SheetExportDestination =
  | { kind: "new_spreadsheet"; title: string }
  | {
      kind: "existing_spreadsheet";
      spreadsheet_id: string;
      target: SheetTabTarget;
    };

export interface ExportToSheetResult {
  spreadsheet_id: string;
  spreadsheet_url: string;
  sheet_title: string;
  rows_written: number;
}

// Mirrors `common_core::gsheet_export::ExportSource` (serde tag = "kind").
// A view carries its full serialized TableView (same struct CSV view export sends).
export type ExportSourceWire =
  | { kind: "table"; table_id: string; version: number }
  | { kind: "view"; version: number; table_view: unknown };

export interface ExportToGoogleSheetParams {
  source: ExportSourceWire;
  savedConnectionId: string;
  destination: SheetExportDestination;
}

/// `POST /action/export_gsheet` — the server FGA-checks, mints a Google token,
/// and forwards to data_service which reads the table/view and writes the sheet.
export const exportToGoogleSheet = async (
  params: ExportToGoogleSheetParams,
): Promise<ExportToSheetResult> => {
  try {
    const res = await axiosInstance.post<ExportToSheetResult>(
      "/action/export_gsheet",
      {
        source: params.source,
        saved_connection_id: params.savedConnectionId,
        destination: params.destination,
      },
    );
    return res.data;
  } catch (error) {
    throw mapExportError(error);
  }
};

function mapExportError(error: unknown): Error {
  if (isReauthorizeRequired(error)) {
    const data = axios.isAxiosError(error)
      ? (error.response?.data as { message?: string } | undefined)
      : undefined;
    return new SheetsReauthorizeRequiredError(data?.message);
  }
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      { error?: string; message?: string } | undefined;
    const serverMsg =
      data?.message ?? data?.error ?? error.response?.statusText;
    if (serverMsg) return new Error(serverMsg);
  }
  return new Error("Failed to export to Google Sheets");
}
